/**
 * ChatTab Component
 * Main chat interface that integrates all chat components
 * Handles message sending, API calls, and state management
 */

import React, { useCallback } from 'react';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { useChatStore } from '@/store/chatStore';
import { useVertexAIQuery } from '@/api/hooks/useChat';

export const ChatTab: React.FC = () => {
  const {
    messages,
    isLoading,
    error,
    addUserMessage,
    addAssistantMessage,
    setLoading,
    setError,
    clearHistory,
    exportToText,
    exportToMarkdown,
    exportToJSON,
  } = useChatStore();

  const { mutateAsync: queryVertexAI } = useVertexAIQuery();

  // Handle sending a message
  const handleSendMessage = useCallback(
    async (message: string) => {
      // Add user message to chat
      addUserMessage(message);
      setLoading(true);
      setError(null);

      try {
        // Query Vertex AI
        const response = await queryVertexAI(message);

        // Add assistant response to chat
        addAssistantMessage(response.regulationText, response.sources);
      } catch (err: any) {
        console.error('Failed to get AI response:', err);
        setError(err.message || 'Failed to get response from AI. Please try again.');

        // Add error message to chat
        addAssistantMessage(
          'I apologize, but I encountered an error while processing your request. Please try again or contact support if the issue persists.',
          []
        );
      } finally {
        setLoading(false);
      }
    },
    [addUserMessage, addAssistantMessage, setLoading, setError, queryVertexAI]
  );

  // Handle exporting chat
  const handleExport = (format: 'text' | 'markdown' | 'json') => {
    let content: string;
    let filename: string;
    let mimeType: string;

    switch (format) {
      case 'markdown':
        content = exportToMarkdown();
        filename = `chat-history-${Date.now()}.md`;
        mimeType = 'text/markdown';
        break;
      case 'json':
        content = exportToJSON();
        filename = `chat-history-${Date.now()}.json`;
        mimeType = 'application/json';
        break;
      default:
        content = exportToText();
        filename = `chat-history-${Date.now()}.txt`;
        mimeType = 'text/plain';
    }

    // Create download link
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle quick action buttons
  const handleQuickAction = (question: string) => {
    handleSendMessage(question);
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">AI Assistant</h3>
              <p className="text-xs text-gray-600">Ask about building codes and regulations</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            {messages.length > 0 && (
              <>
                {/* Export dropdown */}
                <div className="relative group">
                  <button
                    className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    title="Export chat"
                  >
                    Export
                  </button>
                  <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                    <button
                      onClick={() => handleExport('text')}
                      className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 text-gray-700 rounded-t-lg"
                    >
                      Text (.txt)
                    </button>
                    <button
                      onClick={() => handleExport('markdown')}
                      className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 text-gray-700"
                    >
                      Markdown (.md)
                    </button>
                    <button
                      onClick={() => handleExport('json')}
                      className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 text-gray-700 rounded-b-lg"
                    >
                      JSON (.json)
                    </button>
                  </div>
                </div>

                {/* Clear history */}
                <button
                  onClick={clearHistory}
                  className="px-3 py-1.5 text-xs bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  title="Clear chat history"
                >
                  Clear
                </button>
              </>
            )}
          </div>
        </div>

        {/* Quick action buttons */}
        {messages.length === 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => handleQuickAction('What are ADA door width requirements?')}
              className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
            >
              ADA Door Widths
            </button>
            <button
              onClick={() => handleQuickAction('Explain IBC stair handrail requirements')}
              className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
            >
              Stair Handrails
            </button>
            <button
              onClick={() =>
                handleQuickAction('What are accessible toilet room requirements?')
              }
              className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
            >
              Accessible Toilets
            </button>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg
                className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="text-sm font-semibold text-red-800">Error</p>
                <p className="text-xs text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Message list */}
      <MessageList messages={messages} isLoading={isLoading} />

      {/* Input */}
      <MessageInput onSend={handleSendMessage} disabled={isLoading} />
    </div>
  );
};
